import React from 'react';
import { searchContext } from '../../../../context/Context';
import dropdownLists from '../../DropdownLists';
import { renderMappingButton } from '../renders/buttonRenderers';
import { getDiseaseName, getDiseaseLink, getDisease } from '../renders/MappingFunctions';
import { renderDiseaseClassesAttributes, renderDiseaseNameDDA } from '../renders/CommonAtributes';
import { OpenPopup } from '../components';
import { getHeaderProps } from '../config/helpers';
import { createSelectFilter, createCapitalizeFilter } from '../filters/filterConfigs';
import { renderNumericColumn, renderTextColumn } from '@/utils/renderTableValue';

export const getDiseaseColumns = (context: any) => {
  const headerProps = getHeaderProps();
  const { source } = searchContext();

  const sourcesWithoutVariants = ['ORPHANET', 'GENCC', 'MGD_HUMAN', 'RGD_HUMAN'];
  const showNumVariantsAssociatedToDisease = !sourcesWithoutVariants.includes(source);

  return [
    {
      name: 'diseaseName',
      category: 'Disease',
      sortable: true,
      header: <span title="Disease Name">Disease</span>,
      headerProps,
      minWidth: 295,
      render: row => {
        const data = row.data;
        const result = renderTextColumn(getDiseaseName({ data }));
        return <>{result}</>;
      },
    },

    {
      name: 'name',
      category: 'Disease',
      sortable: true,
      header: <span title="Disease Name">Disease</span>,
      headerProps,
      minWidth: 295,
      render: row => {
        const data = row.data;
        const result = renderTextColumn(getDisease({ data }));
        return <>{result}</>;
      },
    },

    {
      name: 'diseaseUMLSCUI',
      category: 'Disease',
      sortable: true,
      header: <span title="Disease UMLS CUI code">Disease CUI</span>,
      headerProps,
      minWidth: 130,
      render: row => {
        const result = renderTextColumn(row.data?.diseaseUMLSCUI);
        if (result === '-') return '-';

        const diseaseList = Array.isArray(result) ? result : [result];

        return (
          <>
            {diseaseList.map((diseaseUMLSCUI, index) => (
              <React.Fragment key={index}>{getDiseaseLink({ diseaseUMLSCUI })}</React.Fragment>
            ))}
          </>
        );
      },
    },

    {
      name: 'diseaseType',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="Disease Type">Disease Type</span> <OpenPopup popupType="showDiseaseType" />
        </>
      ),
      headerProps,
      minWidth: 130,
      ...createSelectFilter(dropdownLists.diseaseType),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'type',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="Disease Type">Disease type</span>
          <OpenPopup popupType="showDiseaseType" />
        </>
      ),
      minWidth: 200,
      headerProps,
      ...createCapitalizeFilter(dropdownLists.type),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'diseaseClassName_DO',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="Disease Ontology Top Class">DO Class</span> <OpenPopup popupType="showDO" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      minWidth: 295,
      ...createCapitalizeFilter(dropdownLists.diseaseClassName_DO),
    },

    {
      name: 'diseaseClassName_HPO',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="Human Phenotye Ontology Top Class">HPO Class</span> <OpenPopup popupType="showHPO" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      minWidth: 295,
      ...createCapitalizeFilter(dropdownLists.diseaseClassName_HPO),
    },

    {
      name: 'diseaseClassName_MSH',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="MeSH Disease Class">MeSH Disease Class</span>
          <OpenPopup popupType="showMeSH" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      minWidth: 230,
      ...createSelectFilter(dropdownLists.diseaseClassName_MSH),
    },

    {
      name: 'diseaseClassName_UMLS',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="UMLS Semantic Type">Semantic Type</span>
          <OpenPopup popupType="showMeSH" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      minWidth: 205,
      ...createSelectFilter(dropdownLists.diseaseClassName_UMLS),
    },

    {
      name: 'disease_inheritance',
      category: 'Disease',
      sortable: true,
      header: <span title="">Disease Inheritance</span>,
      headerProps,
      minWidth: 200,
      ...createSelectFilter(dropdownLists.disease_inheritance),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease_prevalence_class',
      category: 'Disease',
      sortable: true,
      header: <span title="">Prevalence Class</span>,
      headerProps,
      minWidth: 180,
      ...createSelectFilter(dropdownLists.disease_prevalence_class),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease_prevalence_type',
      category: 'Disease',
      sortable: true,
      header: <span title="">Prevalence Type</span>,
      headerProps,
      minWidth: 185,
      ...createSelectFilter(dropdownLists.disease_prevalence_type),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease_prevalence_geo_area',
      category: 'Disease',
      sortable: true,
      header: <span title="">Prevalence Geography</span>,
      headerProps,
      minWidth: 215,
      render: ({ value }) => renderTextColumn(value),
    },

    // Disease 1 columns (DDA)
    {
      name: 'disease1_UMLSCUI',
      category: 'Disease',
      sortable: true,
      header: <span title="Disease UMLS CUI code">Disease CUI 1</span>,
      headerProps,
      minWidth: 205,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease1_Name',
      category: 'Disease',
      sortable: true,
      header: <span title="Index disease">Disease 1</span>,
      headerProps,
      minWidth: 205,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease1_Type',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Type of the index Disease">
          Disease Type <sub>d1</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      ...createSelectFilter(dropdownLists.disease1_Type),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease1ClassName_DO',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Ontology Top Class of the index Disease">
          DO Class <sub>d1</sub>
        </span>
      ),
      headerProps,
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      ...createCapitalizeFilter(dropdownLists.disease1ClassName_DO),
    },

    {
      name: 'disease1ClassName_HPO',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="Human Phenotye Ontology Top Class of the index Disease">
            HPO Class <sub>d1</sub>
          </span>{' '}
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      ...createCapitalizeFilter(dropdownLists.disease1ClassName_HPO),
    },

    {
      name: 'disease1ClassName_MSH',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="MeSH Class of the index Disease">
            MeSH Disease Class <sub>d1</sub>
          </span>{' '}
          <OpenPopup popupType="showMeSH" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      minWidth: 385,
      ...createSelectFilter(dropdownLists.disease1ClassName_MSH),
    },

    {
      name: 'disease1ClassName_UMLS',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="UMLS Semantic Type of the index Disease">
            Semantic Type <sub>d1</sub>
          </span>{' '}
          <OpenPopup popupType="showMeSH" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      ...createSelectFilter(dropdownLists.disease1ClassName_UMLS),
    },

    // Disease 2 columns (DDA)
    {
      name: 'disease2_UMLSCUI',
      category: 'Disease',
      sortable: true,
      header: <span title="Disease UMLS CUI code">Disease CUI 2</span>,
      headerProps,
      minWidth: 205,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease2_Name',
      category: 'Disease',
      sortable: true,
      header: <span title="Disease associated with the index disease">Disease 2</span>,
      headerProps,
      render: instance => renderDiseaseNameDDA(instance, 2),
      minWidth: 295,
    },

    {
      name: 'disease2_Type',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Type of the associated Disease">
          Disease Type <sub>d2</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      ...createSelectFilter(dropdownLists.disease2_Type),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'disease2ClassName_DO',
      category: 'Disease',
      sortable: true,
      header: (
        <span title="Ontology Top Class of the associated Disease">
          DO Class <sub>d2</sub>
        </span>
      ),
      headerProps,
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      ...createCapitalizeFilter(dropdownLists.disease2ClassName_DO),
    },

    {
      name: 'disease2ClassName_HPO',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="Human Phenotye Ontology Top Class of the associated Disease">
            HPO Class <sub>d2</sub>
          </span>{' '}
          <OpenPopup popupType="showHPO" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      ...createCapitalizeFilter(dropdownLists.disease2ClassName_HPO),
    },

    {
      name: 'disease2ClassName_MSH',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="MeSH Class of the associated Disease">
            MeSH Disease Class <sub>d2</sub>
          </span>{' '}
          <OpenPopup popupType="showMeSH" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      minWidth: 385,
      ...createSelectFilter(dropdownLists.disease2ClassName_MSH),
    },

    {
      name: 'disease2ClassName_UMLS',
      category: 'Disease',
      sortable: true,
      header: (
        <>
          <span title="UMLS Semantic Type of the associated Disease">
            Semantic Type <sub>d2</sub>
          </span>{' '}
          <OpenPopup popupType="showMeSH" />
        </>
      ),
      render: ({ value }) => renderDiseaseClassesAttributes(value),
      headerProps,
      ...createSelectFilter(dropdownLists.disease2ClassName_UMLS),
    },

    {
      name: 'numGenesAssociatedToDisease',
      category: 'Disease',
      sortable: true,
      minWidth: 105,
      header: (
        <span title="Number of genes associated to the disease">
          N genes<sub>d</sub>
        </span>
      ),
      headerProps,
      render: ({ data }) => {
        const { tab, setMappingContent } = searchContext();

        const diseaseName = data.diseaseName ?? data.name;
        if (!['DISEASES', 'GDA'].includes(tab) || !data.numGenesAssociatedToDisease) {
          return renderNumericColumn(data?.numGenesAssociatedToDisease);
        }

        return renderMappingButton(
          data,
          {
            displayValue: renderNumericColumn(data.numGenesAssociatedToDisease),
            mappingItems: [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: diseaseName,
                id: data.diseaseUMLSCUI,
              },
            ],
            columVisibilityModelKey: 'numGenesAssociatedToDisease',
            tabSegment: 'gda',
            name: 'numGenesAssociatedToDisease',
            viewSegment: '/summary',
          },
          setMappingContent,
        );
      },
    },

    {
      name: 'numVariantsAssociatedToDisease',
      category: 'Disease',
      sortable: true,
      minWidth: 125,
      header: (
        <span title="Number of variants associated to the disease">
          N variants<sub>d</sub>
        </span>
      ),
      headerProps,
      show: showNumVariantsAssociatedToDisease,
      render: ({ data }) => {
        const { tab, setMappingContent } = searchContext();

        const diseaseName = data.diseaseName ?? data.name;
        if (!['DISEASES', 'GDA'].includes(tab) || !data.numVariantsAssociatedToDisease) {
          return renderNumericColumn(data?.numVariantsAssociatedToDisease);
        }

        return renderMappingButton(
          data,
          {
            displayValue: renderNumericColumn(data.numVariantsAssociatedToDisease),
            mappingItems: [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: diseaseName,
                id: data.diseaseUMLSCUI,
              },
            ],
            columVisibilityModelKey: 'numVariantsAssociatedToDisease',
            tabSegment: 'vda',
            name: 'numVariantsAssociatedToDisease',
            viewSegment: '/summary',
          },
          setMappingContent,
        );
      },
    },
  ];
};
